import React, { useState } from 'react';
import { GlassCard } from '../components/GlassCard';
import { Badge } from '../components/Badge';
import SafeIcon from '../common/SafeIcon';
import { DEMO_WORKFLOW, DEMO_SAMPLE_RECORDS } from '../data/mockData';
import { 
  FiCheckCircle, 
  FiClock, 
  FiAlertTriangle, 
  FiInfo, 
  FiFilter, 
  FiArrowRight, 
  FiLayers, 
  FiShield,
  FiZap,
  FiXCircle,
  FiRotateCw,
  FiPlayCircle
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

export const Demo = () => {
  const [activeStep, setActiveStep] = useState(2);
  const [simState, setSimState] = useState('healthy'); // healthy, alert, loading, empty
  const [records, setRecords] = useState(DEMO_SAMPLE_RECORDS);
  const [filterType, setFilterType] = useState('All');

  const filteredRecords = records.filter(r => filterType === 'All' || r.risk === filterType);

  const handleSimChange = (state) => {
    setSimState('loading');
    setTimeout(() => setSimState(state), 800);
  };

  return (
    <div className="space-y-10 max-w-7xl mx-auto pb-12">
      <header className="space-y-2">
        <div className="flex items-center gap-2 text-emerald-400 font-mono text-xs tracking-tighter uppercase">
          <SafeIcon icon={FiPlayCircle} />
          Interactive Product Tour
        </div>
        <h1 className="text-4xl font-bold tracking-tight">Demo / How It Works</h1>
        <p className="text-slate-400 max-w-2xl text-lg">
          CyberLoom provides a centralized command interface for IT teams to monitor infrastructure posture. 
          Explore the monitoring lifecycle below through our simulated workspace.
        </p>
      </header>

      {/* Try this flow section */}
      <section className="space-y-6">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center border border-emerald-500/20 text-sm">1</span>
          Try this flow
        </h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-4 space-y-3">
            {DEMO_WORKFLOW.map((item, idx) => (
              <button
                key={idx}
                onClick={() => setActiveStep(idx)}
                className={`w-full text-left p-4 rounded-xl border transition-all duration-300 flex items-center gap-4 ${
                  activeStep === idx 
                    ? 'bg-emerald-500/10 border-emerald-500/40 translate-x-2' 
                    : 'bg-white/5 border-white/5 hover:bg-white/10'
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-xs font-mono ${
                  item.status === 'success' ? 'bg-emerald-500/20 text-emerald-400' :
                  item.status === 'in-progress' ? 'bg-blue-500/20 text-blue-400 animate-pulse' :
                  'bg-slate-800 text-slate-500'
                }`}>
                  {item.status === 'success' ? <SafeIcon icon={FiCheckCircle} /> : item.step}
                </div>
                <div className="flex-1">
                  <p className={`text-sm font-medium ${activeStep === idx ? 'text-emerald-400' : 'text-slate-300'}`}>{item.title}</p>
                </div>
                {activeStep === idx && <SafeIcon icon={FiArrowRight} className="text-emerald-500" />}
              </button>
            ))}
          </div>

          <GlassCard className="lg:col-span-8 flex flex-col justify-center min-h-[300px] relative overflow-hidden">
            <div className="absolute top-0 right-0 p-8 opacity-5">
              <SafeIcon icon={FiShield} className="text-[200px]" />
            </div>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6 relative z-10"
              >
                <div className="space-y-2">
                  <Badge type={DEMO_WORKFLOW[activeStep].status === 'success' ? 'passed' : 'warning'} text={DEMO_WORKFLOW[activeStep].status.replace('-', ' ').toUpperCase()} />
                  <h3 className="text-2xl font-bold">{DEMO_WORKFLOW[activeStep].title}</h3>
                  <p className="text-slate-400 leading-relaxed text-lg">
                    {DEMO_WORKFLOW[activeStep].description}
                  </p>
                </div>

                <div className="flex flex-wrap gap-4 pt-4">
                  <button className="px-6 py-2.5 bg-emerald-500 text-black rounded-lg font-bold hover:bg-emerald-400 transition-colors flex items-center gap-2">
                    <SafeIcon icon={FiZap} />
                    {DEMO_WORKFLOW[activeStep].action}
                  </button>
                  <button className="px-6 py-2.5 bg-white/5 border border-white/10 rounded-lg font-medium hover:bg-white/10 transition-colors">
                    Learn more
                  </button>
                </div>
              </motion.div>
            </AnimatePresence>
          </GlassCard>
        </div>
      </section>

      {/* Simulated States section */}
      <section className="space-y-6">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-red-500/10 text-red-400 flex items-center justify-center border border-red-500/20 text-sm">2</span>
          Simulated States
        </h2>
        
        <div className="flex flex-wrap gap-3">
          {[
            { id: 'healthy', label: 'Success (Healthy)', icon: FiCheckCircle, color: 'text-emerald-400' },
            { id: 'alert', label: 'Warning (Alerts)', icon: FiAlertTriangle, color: 'text-red-400' },
            { id: 'loading', label: 'In Progress', icon: FiRotateCw, color: 'text-blue-400' },
            { id: 'empty', label: 'Empty State', icon: FiLayers, color: 'text-slate-400' }
          ].map(state => (
            <button
              key={state.id}
              onClick={() => handleSimChange(state.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm transition-all ${
                simState === state.id 
                  ? 'bg-white/10 border-white/20 ring-1 ring-white/10' 
                  : 'bg-black/40 border-white/5 text-slate-500 hover:border-white/10'
              }`}
            >
              <SafeIcon icon={state.icon} className={simState === state.id ? state.color : ''} />
              {state.label}
            </button>
          ))}
        </div>

        <GlassCard className="min-h-[400px] flex items-center justify-center">
          <AnimatePresence mode="wait">
            {simState === 'loading' && (
              <motion.div 
                key="loading"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="text-center space-y-4"
              >
                <div className="w-12 h-12 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin mx-auto" />
                <p className="text-emerald-400 font-mono text-sm animate-pulse">ANALYZING INFRASTRUCTURE...</p>
              </motion.div>
            )}

            {simState === 'healthy' && (
              <motion.div 
                key="healthy"
                initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                className="text-center space-y-4 max-w-md"
              >
                <div className="w-16 h-16 bg-emerald-500/10 text-emerald-500 rounded-full flex items-center justify-center mx-auto border border-emerald-500/20">
                  <SafeIcon icon={FiCheckCircle} className="text-3xl" />
                </div>
                <h3 className="text-xl font-bold">Posture Stabilized</h3>
                <p className="text-slate-400">All monitored assets are currently compliant with baseline security policies. No critical threats detected in the last 24 hours.</p>
                <button className="text-emerald-400 text-sm font-medium hover:underline">Download Compliance Summary</button>
              </motion.div>
            )}

            {simState === 'alert' && (
              <motion.div 
                key="alert"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="w-full space-y-6"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-500/10 text-red-500 rounded">
                      <SafeIcon icon={FiAlertTriangle} />
                    </div>
                    <h3 className="text-lg font-bold">Threat Posture: Critical</h3>
                  </div>
                  <Badge type="critical" text="ACTION REQUIRED" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[1, 2].map(i => (
                    <div key={i} className="p-4 bg-red-500/5 border border-red-500/20 rounded-xl space-y-2">
                      <div className="flex justify-between items-start">
                        <span className="text-xs font-mono text-red-400">CVE-2024-X{i}</span>
                        <Badge type="critical" />
                      </div>
                      <p className="font-medium text-sm">Critical kernel vulnerability detected on Linux nodes.</p>
                      <button className="text-xs text-red-400 hover:underline">View Remediation Plan</button>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {simState === 'empty' && (
              <motion.div 
                key="empty"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="text-center space-y-4"
              >
                <div className="w-16 h-16 bg-white/5 text-slate-600 rounded-full flex items-center justify-center mx-auto">
                  <SafeIcon icon={FiLayers} className="text-3xl" />
                </div>
                <h3 className="text-xl font-bold text-slate-400">No Assets Monitored</h3>
                <p className="text-slate-500 max-w-xs mx-auto">Start by connecting your first cloud provider or deploying an agent to see posture data.</p>
                <button className="px-4 py-2 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-lg text-sm">Get Started</button>
              </motion.div>
            )}
          </AnimatePresence>
        </GlassCard>
      </section>

      {/* Sample Data section */}
      <section className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-blue-500/10 text-blue-400 flex items-center justify-center border border-blue-500/20 text-sm">3</span>
            Sample Data
          </h2>
          
          <div className="flex items-center gap-2 bg-white/5 p-1 rounded-lg border border-white/10">
            {['All', 'Critical', 'High', 'Medium'].map(type => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`px-3 py-1 text-xs rounded-md transition-all ${
                  filterType === type ? 'bg-emerald-500 text-black font-bold' : 'text-slate-400 hover:text-white'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        <GlassCard className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-black/40 border-b border-white/10 text-slate-400 uppercase text-[10px] tracking-wider">
                <tr>
                  <th className="px-6 py-4">ID</th>
                  <th className="px-6 py-4">Asset Name</th>
                  <th className="px-6 py-4">Category</th>
                  <th className="px-6 py-4">Risk Level</th>
                  <th className="px-6 py-4">Last Event</th>
                  <th className="px-6 py-4">Internal Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filteredRecords.map((rec) => (
                  <tr key={rec.id} className="hover:bg-white/[0.02] transition-colors group">
                    <td className="px-6 py-4 font-mono text-xs text-slate-500">{rec.id}</td>
                    <td className="px-6 py-4 font-medium">{rec.name}</td>
                    <td className="px-6 py-4 text-slate-400">{rec.category}</td>
                    <td className="px-6 py-4">
                      <Badge type={rec.risk.toLowerCase()} />
                    </td>
                    <td className="px-6 py-4 text-slate-500 text-xs">{rec.date}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-xs text-slate-400 italic">
                        <SafeIcon icon={FiInfo} className="shrink-0" />
                        <span className="truncate max-w-[200px]">{rec.note}</span>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredRecords.length === 0 && (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-slate-500 italic">
                      No records match the current filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </GlassCard>
      </section>

      {/* Final Disclaimer/Summary */}
      <footer className="pt-8 border-t border-white/5">
        <div className="p-6 bg-emerald-500/5 rounded-2xl border border-emerald-500/10 flex flex-col md:flex-row items-center gap-6">
          <div className="w-12 h-12 bg-emerald-500/10 text-emerald-400 rounded-xl flex items-center justify-center shrink-0">
             <SafeIcon icon={FiShield} className="text-2xl" />
          </div>
          <div className="flex-1">
            <h4 className="font-bold text-emerald-400">Security Posture Monitoring</h4>
            <p className="text-sm text-slate-400 mt-1 leading-relaxed">
              CyberLoom provides visibility into potential security gaps. It is a posture monitoring tool and does not provide active threat prevention, malware removal, or guarantees of total protection. Always consult with certified security professionals for critical infrastructure hardening.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};